<template>
  <div class="bg-white overflow-hidden overflow-y-auto">
    <div v-if="loading" class="p-4 animate-pulse space-y-2">
      <div class="h-10 bg-gray-100 rounded"></div>
      <div class="h-10 bg-gray-100 rounded"></div>
      <div class="h-10 bg-gray-100 rounded"></div>
    </div>

    <div v-else-if="!applications.length" class="p-8 text-center text-sm text-gray-500">
      No referral applications found.
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full" :class="{ 'min-w-[1120px]': !applicationExpanded }">
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr class="text-xs uppercase tracking-wider text-gray-500">
            <th v-if="!applicationExpanded" class="px-4 py-3 text-left">Updated</th>
            <th v-if="!applicationExpanded" class="px-4 py-3 text-left">Applicant</th>
            <th class="px-4 py-3 text-left">Role & Company</th>
            <th v-if="!applicationExpanded" class="px-4 py-3 text-left">Application status</th>
            <th v-if="!applicationExpanded" class="px-4 py-3 text-left">Interview stage</th>
            <th v-if="!applicationExpanded" class="px-4 py-3 text-left">Referral status</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr
            v-for="application in applications"
            :key="application.id"
            class="hover:bg-gray-50 cursor-pointer"
            :class="{ 'bg-gray-100': currentApplicationId === application.id }"
            @click="navigateToApplication(application.id)"
          >
            <td v-if="!applicationExpanded" class="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
              {{ formatDate(application.updated_at || application.date_applied) }}
            </td>
            <td v-if="!applicationExpanded" class="px-4 py-3">
              <div class="flex items-center gap-2 min-w-[220px]">
                <img
                  v-if="application.applicant.avatar_image"
                  :src="application.applicant.avatar_image"
                  alt=""
                  class="w-8 h-8 rounded-full object-cover"
                />
                <div
                  v-else
                  class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600"
                >
                  {{ initials(application.applicant.first_name, application.applicant.last_name) }}
                </div>
                <div class="min-w-0">
                  <p class="text-sm text-gray-900 truncate">
                    {{ application.applicant.first_name }} {{ application.applicant.last_name }}
                  </p>
                  <p class="text-xs text-gray-500 truncate">{{ application.applicant.email }}</p>
                </div>
              </div>
            </td>
            <td class="px-4 py-3" :class="{ 'py-3 px-4': applicationExpanded }">
              <div class="min-w-0">
                <div v-if="applicationExpanded" class="flex items-center gap-2 mb-2">
                  <img
                    v-if="application.applicant.avatar_image"
                    :src="application.applicant.avatar_image"
                    alt=""
                    class="h-4 w-4 rounded-full object-cover"
                  />
                  <div
                    v-else
                    class="h-4 w-4 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-600"
                  >
                    {{ initials(application.applicant.first_name, application.applicant.last_name) }}
                  </div>
                  <span class="text-xs text-gray-600 truncate">
                    {{ application.applicant.first_name }} {{ application.applicant.last_name }}
                  </span>
                </div>
                <div class="flex items-center" :class="{ 'min-w-[280px]': !applicationExpanded }">
                  <img
                    v-if="application.open_role_details?.employer_details?.logo"
                    :src="application.open_role_details.employer_details.logo"
                    class="h-10 w-10 rounded-lg mr-3 flex-shrink-0"
                    :class="{ 'h-6 w-6': applicationExpanded }"
                    alt=""
                  />
                  <div
                    v-else
                    class="rounded-lg bg-gray-200 mr-3 flex items-center justify-center flex-shrink-0"
                    :class="applicationExpanded ? 'h-6 w-6' : 'h-10 w-10'"
                  >
                    <Building2 :class="applicationExpanded ? 'h-3 w-3' : 'h-5 w-5'" class="text-gray-400" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate max-w-[350px]">
                      {{ application.open_role_details?.role_name || '-' }}
                    </p>
                    <p class="text-xs text-gray-500 truncate">
                      {{ application.open_role_details?.employer_details?.name || '-' }}
                    </p>
                  </div>
                </div>
              </div>
            </td>

            <td v-if="!applicationExpanded" class="px-4 py-3 min-w-[150px]">
              <span
                :class="getStatusClasses('application', application.application_status)"
                class="px-2 py-1 rounded-full text-xs truncate max-w-full"
              >
                {{ getStatusLabel('application', application.application_status) }}
              </span>
            </td>
            <td v-if="!applicationExpanded" class="px-4 py-3 min-w-[150px]">
              <span
                :class="getStatusClasses('interview', application.interview_status)"
                class="px-2 py-1 rounded-full text-xs"
              >
                {{ getStatusLabel('interview', application.interview_status) }}
              </span>
            </td>
            <td v-if="!applicationExpanded" class="px-4 py-3 min-w-[300px]" @click.stop>
              <Select
                :model-value="application.referral_status"
                @update:model-value="
                  (value) => handleStatusChange(application.id, String(value || ''))
                "
              >
                <SelectTrigger
                  class="border-0 shadow-none hover:bg-gray-100"
                  :disabled="isDeadendStatus(application.referral_status)"
                >
                  <SelectValue>
                    <span
                      :class="getEditableReferralStatusClasses(application.referral_status)"
                      class="px-2 py-1 rounded-full text-xs truncate w-full block"
                    >
                      {{ getEditableReferralStatusLabel(application.referral_status) }}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="status in editableStatuses"
                    :key="status.value"
                    :value="status.value"
                  >
                    <span
                      :class="getEditableReferralStatusClasses(status.value)"
                      class="px-2 py-1 rounded-full text-xs"
                    >
                      {{ status.label }}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PaginationComponent
      v-if="applications.length && totalPages > 1"
      :current-page="currentPage"
      :total-pages="totalPages"
      :use-router="false"
      @update:page="handlePageChange"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ReferrerAllowedStatus, ReferrerApplication } from '@/types/referrerDashboard'
import { Building2 } from 'lucide-vue-next'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralStatusOptions,
  referrerEditableStatusValues,
  referrerEditableStatusOptions
} from '@/const/applicationStatuses'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useRoute, useRouter } from 'vue-router'

const props = withDefaults(
  defineProps<{
    applications: ReferrerApplication[]
    loading: boolean
    currentPage: number
    totalPages: number
    applicationExpanded: boolean
  }>(),
  {
    applications: () => [],
    currentPage: 1,
    totalPages: 1,
    applicationExpanded: false
  }
)

const emit = defineEmits<{
  updateReferralStatus: [applicationId: number, status: ReferrerAllowedStatus]
  updatePage: [page: number]
}>()
const router = useRouter()
const route = useRoute()

const currentApplicationId = computed(() => {
  const id = route.params?.application_id ? Number(route.params.application_id) : null
  return id
})

const editableStatuses = referrerEditableStatusOptions.map((opt) => ({
  value: opt.value as ReferrerAllowedStatus,
  label: opt.label
}))

function initials(firstName: string, lastName: string) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
}

function formatDate(dateString: string | null | undefined) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

function getStatusLabel(type: 'application' | 'interview', value: string | null) {
  if (!value) return 'Not set'
  const options = type === 'application' ? applicationStatusOptions : interviewStatusOptions
  return options.find((option) => option.value === value)?.label || value
}

function getStatusClasses(type: 'application' | 'interview', value: string | null) {
  if (!value) return ['bg-gray-100', 'text-gray-700']
  const options = type === 'application' ? applicationStatusOptions : interviewStatusOptions
  const option = options.find((item) => item.value === value)
  return option ? [option.bgColor, option.textColor] : ['bg-gray-100', 'text-gray-700']
}

function getEditableReferralStatusLabel(value: string | null) {
  if (!value) return 'Not set'
  return referralStatusOptions.find((status) => status.value === value)?.label || value
}

function isDeadendStatus(value: string | null) {
  if (!value) return false
  return referralStatusOptions.find((status) => status.value === value)?.deadend === true
}

function getEditableReferralStatusClasses(value: string | null) {
  if (!value) return ['bg-gray-100', 'text-gray-700']
  const option = referralStatusOptions.find((item) => item.value === value)
  return option
    ? [option.bgColor || 'bg-gray-100', option.textColor || 'text-gray-700']
    : ['bg-gray-100', 'text-gray-700']
}

function handleStatusChange(applicationId: number, status: string) {
  if (!referrerEditableStatusValues.includes(status as ReferrerAllowedStatus)) {
    return
  }
  emit('updateReferralStatus', applicationId, status as ReferrerAllowedStatus)
}

function handlePageChange(page: number) {
  if (page < 1 || page > props.totalPages) return
  emit('updatePage', page)
}

function navigateToApplication(applicationId: number) {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    name: 'My Referrals Expanded Application',
    params: { application_id: String(applicationId) },
    query: {
      ...route.query
    }
  })
}
</script>
